import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import HomePage from './components/HomePage';
import SetupPage from './components/SetupPage';
import QuickSetupPage from './components/QuickSetupPage';
import FixedDurationPage from './components/FixedDurationPage';
import TrackerPage from './components/TrackerPage';
import ResultsPage from './components/ResultsPage';
import HistoryPage from './components/HistoryPage';
import { Storage } from './utils/storage';

// Wrapper component to handle navigation and analytics
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [participants, setParticipants] = useState([]);
  const [meetingData, setMeetingData] = useState(null);
  const [history, setHistory] = useState([]);
  const [currency, setCurrency] = useState('USD');
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);

  useEffect(() => {
    setHistory(Storage.getHistory());
    setCurrency(Storage.getCurrency());
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
    Storage.setCurrency(newCurrency);
  };

  const navigateTo = (page, data = null) => {
    if (data) {
      setMeetingData(data);
    } else {
      setMeetingData(null);
    }
    
    // Map old page names to routes
    const routeMap = {
      'home': '/',
      'setup': '/setup',
      'quick-setup': '/quick-setup',
      'fixed-duration': '/fixed-duration',
      'tracker': '/tracker',
      'results': '/results',
      'history': '/history'
    };
    
    navigate(routeMap[page] || '/');
  };

  const viewMeetingDetails = (meetingId) => {
    setSelectedMeetingId(meetingId);
    navigate('/history');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <Routes>
        <Route 
          path="/" 
          element={
            <HomePage 
              history={history} 
              navigateTo={navigateTo} 
              setHistory={setHistory} 
              currency={currency} 
              viewMeetingDetails={viewMeetingDetails} 
            />
          } 
        />
        <Route 
          path="/setup" 
          element={
            <SetupPage 
              navigateTo={navigateTo} 
              setParticipants={setParticipants} 
              initialData={meetingData} 
              currency={currency} 
              onCurrencyChange={handleCurrencyChange} 
            />
          } 
        />
        <Route 
          path="/quick-setup" 
          element={
            <QuickSetupPage 
              navigateTo={navigateTo} 
              setParticipants={setParticipants} 
              initialData={meetingData}
              currency={currency} 
              onCurrencyChange={handleCurrencyChange} 
            />
          } 
        />
        <Route 
          path="/fixed-duration" 
          element={
            <FixedDurationPage 
              navigateTo={navigateTo} 
              currency={currency} 
              onCurrencyChange={handleCurrencyChange}
              initialData={meetingData}
            />
          } 
        />
        <Route 
          path="/tracker" 
          element={
            <TrackerPage 
              participants={participants} 
              navigateTo={navigateTo} 
              currency={currency} 
            />
          } 
        />
        <Route 
          path="/results" 
          element={
            <ResultsPage 
              meetingData={meetingData} 
              navigateTo={navigateTo} 
              setHistory={setHistory} 
              currency={currency} 
            />
          } 
        />
        <Route 
          path="/history" 
          element={
            <HistoryPage 
              history={history} 
              setHistory={setHistory} 
              navigateTo={navigateTo} 
              currency={currency} 
              selectedMeetingId={selectedMeetingId} 
              setSelectedMeetingId={setSelectedMeetingId} 
            />
          } 
        />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}